import React, { useState, useEffect } from "react";
import api from "../../utils/api";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { fetchWorkers } from "../../redux/workerSlice";
import { Skeleton, Modal, Button, message } from "antd";
import { PlusOutlined, SolutionOutlined } from "@ant-design/icons";
import { NepaliDatePicker } from "nepali-datepicker-reactjs";
import NepaliDate from "nepali-date-converter";
import "nepali-datepicker-reactjs/dist/index.css";

const Attendance = () => {
  const dispatch = useDispatch();
  const { workers, loading } = useSelector((state) => state.workers);

  // Add Attendance Modal States
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [startTimeNepali, setStartTimeNepali] = useState(""); // BS date string
  const [startTimeHour, setStartTimeHour] = useState("");     // Time part
  const [startTimeAD, setStartTimeAD] = useState("");         // Final AD datetime
  const [endTimeNepali, setEndTimeNepali] = useState("");
  const [endTimeHour, setEndTimeHour] = useState("");
  const [endTimeAD, setEndTimeAD] = useState("");
  const [isSending, setIsSending] = useState(false);

  // Action Modal States
  const [isActionModalVisible, setIsActionModalVisible] = useState(false);
  const [actionWorker, setActionWorker] = useState(null);
  const [actionStartTimeNepali, setActionStartTimeNepali] = useState("");
  const [actionStartTimeHour, setActionStartTimeHour] = useState("");
  const [actionStartTimeAD, setActionStartTimeAD] = useState("");
  const [actionEndTimeNepali, setActionEndTimeNepali] = useState("");
  const [actionEndTimeHour, setActionEndTimeHour] = useState("");
  const [actionEndTimeAD, setActionEndTimeAD] = useState("");

  const [errors, setErrors] = useState({
    worker: "",
    startTime: "",
    endTime: "¿",
  });

  useEffect(() => {
    dispatch(fetchWorkers());
  }, [dispatch]);

  // Convert BS date + time to AD datetime string
  const convertBSToAD = (bsDate, time = "00:00") => {
    try {
      if (!bsDate) return "";
      const [year, month, day] = bsDate.split("-").map(Number);
      const nepaliDate = new NepaliDate(year, month - 1, day);
      const adDate = nepaliDate.toJsDate();
      const [hours, minutes] = time.split(":").map(Number);
      adDate.setHours(hours, minutes, 0, 0);
      return adDate.toISOString().slice(0, 16); // YYYY-MM-DDTHH:MM
    } catch (err) {
      console.error("Date conversion error:", err);
      return "";
    }
  };

  // Format datetime for API (YYYY-MM-DD hh:mm AM/PM)
  const formatDateForAPI = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    const formattedHours = String(hours).padStart(2, "0");
    return `${year}-${month}-${day} ${formattedHours}:${minutes} ${ampm}`;
  };

  // Validation
  const validateForm = (worker, start, end) => {
    let isValid = true;
    const newErrors = { worker: "", startTime: "", endTime: "" };

    if (!worker) {
      newErrors.worker = "Please select a worker.";
      isValid = false;
    }
    if (!start) {
      newErrors.startTime = "Start time is required.";
      isValid = false;
    }
    if (!end) {
      newErrors.endTime = "End time is required.";
      isValid = false;
    } else if (new Date(end) <= new Date(start)) {
      newErrors.endTime = "End time must be after start time.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Submit Handler
  const handleSubmit = async (e, worker, start, end, onSuccess) => {
    e.preventDefault();
    setIsSending(true);

    if (!validateForm(worker, start, end)) {
      setIsSending(false);
      return;
    }

    const payload = {
      user_id: worker.value,
      start_time: formatDateForAPI(start),
      end_time: formatDateForAPI(end),
    };

    try {
      await api.post("/attendances", payload);
      message.success("Attendance recorded successfully!");
      onSuccess();
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to record attendance.");
      console.error("Error submitting attendance:", error);
    } finally {
      setIsSending(false);
    }
  };

  // Modal Handlers
  const openAddModal = () => {
    setSelectedWorker(null);
    setStartTimeNepali("");
    setStartTimeHour("");
    setStartTimeAD("");
    setEndTimeNepali("");
    setEndTimeHour("");
    setEndTimeAD("");
    setErrors({ worker: "", startTime: "", endTime: "" });
    setIsAddModalVisible(true);
  };

  const openActionModal = (worker) => {
    setActionWorker({ value: worker.id, label: worker.name });
    setActionStartTimeNepali("");
    setActionStartTimeHour("");
    setActionStartTimeAD("");
    setActionEndTimeNepali("");
    setActionEndTimeHour("");
    setActionEndTimeAD("");
    setErrors({ worker: "", startTime: "", endTime: "" });
    setIsActionModalVisible(true);
  };

  const closeAddModal = () => {
    setIsAddModalVisible(false);
    setSelectedWorker(null);
    setStartTimeNepali("");
    setStartTimeHour("");
    setStartTimeAD("");
    setEndTimeNepali("");
    setEndTimeHour("");
    setEndTimeAD("");
  };

  const closeActionModal = () => {
    setIsActionModalVisible(false);
    setActionWorker(null);
    setActionStartTimeNepali("");
    setActionStartTimeHour("");
    setActionStartTimeAD("");
    setActionEndTimeNepali("");
    setActionEndTimeHour("");
    setActionEndTimeAD("");
  };

  const workerOptions = workers.map((w) => ({
    value: w.id,
    label: w.name,
  }));

  return (
    <div className="w-full bg-slate-200 p-6 pb-20 rounded-lg shadow-md">
      <div className="bg-white rounded-lg p-4">
        <div className="mb-4 flex justify-between">
          <h1 className="text-xl font-semibold mb-4">Attendance</h1>
          <Button
            type="default"
            className="bg-green-800 text-white hover:bg-green-600"
            icon={<PlusOutlined />}
            onClick={openAddModal}
          >
            Add Attendance
          </Button>
        </div>

        <div className="bg-gray-200 p-5 rounded-lg">
          {loading ? (
            <div className="p-12">
              <Skeleton active />
            </div>
          ) : (
            <div className="bg-white p-4 rounded-lg overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="text-left">
                    <th className="py-2 px-4 border-r border-b border-gray-300">ID</th>
                    <th className="py-2 px-4 border-r border-b border-gray-300">Name</th>
                    <th className="py-2 px-4 border-b border-gray-300">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {workers.map((worker) => (
                    <tr key={worker.id} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-4 border-r border-gray-300">{worker.id}</td>
                      <td className="py-2 px-4 border-r border-gray-300">{worker.name}</td>
                      <td className="py-2 px-4">
                        <Button
                          type="default"
                          className="bg-blue-800 text-white hover:bg-blue-600"
                          onClick={() => openActionModal(worker)}
                          icon={<SolutionOutlined />}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ========== ADD ATTENDANCE MODAL ========== */}
      <Modal
        title="Add New Attendance"
        open={isAddModalVisible}
        onCancel={closeAddModal}
        footer={null}
        destroyOnClose
      >
        <form
          onSubmit={(e) =>
            handleSubmit(e, selectedWorker, startTimeAD, endTimeAD, closeAddModal)
          }
        >
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Select Worker</label>
            <Select
              options={workerOptions}
              value={selectedWorker}
              onChange={(val) => {
                setSelectedWorker(val);
                setErrors((prev) => ({ ...prev, worker: "" }));
              }}
              placeholder="Choose a worker..."
              isSearchable
            />
            {errors.worker && <p className="text-red-500 text-sm mt-1">{errors.worker}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Start Date (BS)</label>
              <NepaliDatePicker
               options={{ calenderLocale: "en", valueLocale: "en" }}
                value={startTimeNepali}
                onChange={(value) => {
                  setStartTimeNepali(value);
                  const adDateTime = convertBSToAD(value, startTimeHour || "00:00");
                  setStartTimeAD(adDateTime);
                  setErrors((prev) => ({ ...prev, startTime: "" }));
                }}
                className="w-full rounded-md border border-gray-400 py-4 px-3"
              />
              {errors.startTime && <p className="text-red-500 text-sm mt-1">{errors.startTime}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Start Time</label>
              <input
                type="time"
                value={startTimeHour}
                onChange={(e) => {
                  const time = e.target.value;
                  setStartTimeHour(time);
                  const adDateTime = convertBSToAD(startTimeNepali || "2082-01-01", time);
                  setStartTimeAD(adDateTime);
                  setErrors((prev) => ({ ...prev, startTime: "" }));
                }}
                className="w-full rounded-md border border-gray-400 py-2 px-3"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-1">End Date (BS)</label>
              <NepaliDatePicker
               options={{ calenderLocale: "en", valueLocale: "en" }}
                value={endTimeNepali}
                onChange={(value) => {
                  setEndTimeNepali(value);
                  const adDateTime = convertBSToAD(value, endTimeHour || "00:00");
                  setEndTimeAD(adDateTime);
                  setErrors((prev) => ({ ...prev, endTime: "" }));
                }}
                className="w-full rounded-md border border-gray-400 py-4 px-3"
              />
              {errors.endTime && <p className="text-red-500 text-sm mt-1">{errors.endTime}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">End Time</label>
              <input
                type="time"
                value={endTimeHour}
                onChange={(e) => {
                  const time = e.target.value;
                  setEndTimeHour(time);
                  const adDateTime = convertBSToAD(endTimeNepali || "2082-01-01", time);
                  setEndTimeAD(adDateTime);
                  setErrors((prev) => ({ ...prev, endTime: "" }));
                }}
                className="w-full rounded-md border border-gray-400 py-2 px-3"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <Button
              type="primary"
              htmlType="submit"
              loading={isSending}
              className="bg-[#403F93] hover:bg-[#35307a]"
            >
              Submit Attendance
            </Button>
          </div>
        </form>
      </Modal>

      {/* ========== ACTION MODAL ========== */}
      <Modal
        title={`Set Attendance for ${actionWorker?.label}`}
        open={isActionModalVisible}
        onCancel={closeActionModal}
        footer={null}
        destroyOnClose
      >
        <form
          onSubmit={(e) =>
            handleSubmit(e, actionWorker, actionStartTimeAD, actionEndTimeAD, closeActionModal)
          }
        >
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Worker</label>
            <p className="text-lg font-medium">{actionWorker?.label}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Start Date (BS)</label>
              <NepaliDatePicker
               options={{ calenderLocale: "en", valueLocale: "en" }}
                value={actionStartTimeNepali}
                onChange={(value) => {
                  setActionStartTimeNepali(value);
                  const adDateTime = convertBSToAD(value, actionStartTimeHour || "00:00");
                  setActionStartTimeAD(adDateTime);
                  setErrors((prev) => ({ ...prev, startTime: "" }));
                }}
                className="w-full rounded-md border border-gray-400 py-4 px-3"
              />
              {errors.startTime && <p className="text-red-500 text-sm mt-1">{errors.startTime}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Start Time</label>
              <input
                type="time"
                value={actionStartTimeHour}
                onChange={(e) => {
                  const time = e.target.value;
                  setActionStartTimeHour(time);
                  const adDateTime = convertBSToAD(actionStartTimeNepali || "2082-01-01", time);
                  setActionStartTimeAD(adDateTime);
                  setErrors((prev) => ({ ...prev, startTime: "" }));
                }}
                className="w-full rounded-md border border-gray-400 py-2 px-3"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-1">End Date (BS)</label>
              <NepaliDatePicker
               options={{ calenderLocale: "en", valueLocale: "en" }}
                value={actionEndTimeNepali}
                onChange={(value) => {
                  setActionEndTimeNepali(value);
                  const adDateTime = convertBSToAD(value, actionEndTimeHour || "00:00");
                  setActionEndTimeAD(adDateTime);
                  setErrors((prev) => ({ ...prev, endTime: "" }));
                }}
                className="w-full rounded-md border border-gray-400 py-4 px-3"
              />
              {errors.endTime && <p className="text-red-500 text-sm mt-1">{errors.endTime}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">End Time</label>
              <input
                type="time"
                value={actionEndTimeHour}
                onChange={(e) => {
                  const time = e.target.value;
                  setActionEndTimeHour(time);
                  const adDateTime = convertBSToAD(actionEndTimeNepali || "2082-01-01", time);
                  setActionEndTimeAD(adDateTime);
                  setErrors((prev) => ({ ...prev, endTime: "" }));
                }}
                className="w-full rounded-md border border-gray-400 py-2 px-3"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <Button
              type="primary"
              htmlType="submit"
              loading={isSending}
              className="bg-[#403F93] hover:bg-[#35307a]"
            >
              Submit Attendance
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Attendance;